<template>
    <v-row dense class="px-1">
        <v-col cols="12" md="6">
            <div class="text-subtitle-1 font-weight-medium">{{ $t('starting_location') }}*</div>
            <div
                v-if="isShowValidationError && !contract.departureLocations?.length"
                class="text-subtitle-2 pt-1 text-red-darken-4 font-weight-regular"
            >
                {{ `${$t('starting_location')} ${$t('validation.required')}` }}
            </div>
            <v-data-table
                :headers="useHeaders"
                :items="contract.departureLocations"
                disable-sort
                density="compact"
                color="primary"
                class="elevation-1 table-server mb-2"
                data-cy="table"
                :items-per-page="-1"
                hide-default-footer
            >
                <template v-slot:item.name="{item}">
                    <span
                        v-if="serviceType === contractServiceType.ownExcursion"
                        class="text-subtitle-2 text-decoration-underline text-link"
                        :data-cy="`add-start-location-${item.id}`"
                        @click="addLocation('departure', item)"
                    >
                        {{ getName(item) }}
                    </span>
                    <span v-if="serviceType === contractServiceType.ownTransfer">{{ getName(item) }}</span>
                    <v-icon
                        v-if="serviceType === contractServiceType.ownTransfer && item.type === StartEndLocation.Hotel"
                        class="cursor-pointer ml-2"
                        color="primary"
                        :data-cy="`select-point-for-${item.id}`"
                        @click="selectHotel(item)"
                    >
                        mdi-office-building-plus-outline
                    </v-icon>
                    <span v-if="item.hotelIds?.length">
                        ({{ item.hotelIds.length }} {{ item.hotelIds.length === 1 ? $t('hotel', 1) : $t('hotel', 2) }})
                    </span>
                </template>
                <template v-slot:item.timeShift="{item}"> + {{ item.timeShift || '' }} </template>
                <template v-slot:item.action="{item}">
                    <v-btn
                        variant="text"
                        color="primary"
                        class="mr-4 text-caption"
                        :data-cy="`delete-departure-location-${item.id}`"
                        density="compact"
                        @click="deleteDepartureLocation(item.id)"
                    >
                        {{ $t('delete') }}
                    </v-btn>
                </template>
            </v-data-table>
            <v-btn color="primary" class="mr-4" data-cy="add" @click="addLocation('departure')">
                {{ $t('add') }}
            </v-btn>
        </v-col>
        <v-col cols="12" md="6">
            <div class="text-subtitle-1 font-weight-medium">{{ $t('ending_location') }}*</div>
            <div
                v-if="isShowValidationError && !contract.destinationLocations?.length"
                class="text-subtitle-2 pt-1 text-red-darken-4 font-weight-regular"
            >
                {{ `${$t('ending_location')} ${$t('validation.required')}` }}
            </div>
            <v-data-table
                :headers="useHeaders"
                :items="contract.destinationLocations"
                disable-sort
                density="compact"
                color="primary"
                class="elevation-1 table-server mb-2"
                data-cy="table"
                :items-per-page="-1"
                hide-default-footer
            >
                <template v-slot:item.name="{item}">
                    <span
                        v-if="serviceType === contractServiceType.ownExcursion"
                        class="text-subtitle-2 text-decoration-underline text-link"
                        :data-cy="`add-end-location-${item.id}`"
                        @click="addLocation('destination', item)"
                    >
                        {{ getName(item) }}
                    </span>
                    <span v-if="serviceType === contractServiceType.ownTransfer">{{ getName(item) }}</span>
                    <v-icon
                        v-if="serviceType === contractServiceType.ownTransfer && item.type === StartEndLocation.Hotel"
                        class="cursor-pointer ml-2"
                        color="primary"
                        :data-cy="`select-point-for-${item.id}`"
                        @click="selectHotel(item)"
                    >
                        mdi-office-building-plus-outline
                    </v-icon>
                    <span v-if="item.hotelIds?.length">
                        ({{ item.hotelIds.length }} {{ item.hotelIds.length === 1 ? $t('hotel', 1) : $t('hotel', 2) }})
                    </span>
                </template>
                <template v-slot:item.timeShift="{item}"> + {{ item.timeShift || '' }} </template>
                <template v-slot:item.action="{item}">
                    <v-btn
                        variant="text"
                        color="primary"
                        class="mr-4 text-caption"
                        :data-cy="`delete-destination-location-${item.id}`"
                        density="compact"
                        @click="deleteDestinationLocation(item.id)"
                    >
                        {{ $t('delete') }}
                    </v-btn>
                </template>
            </v-data-table>
            <v-btn color="primary" class="mr-4" data-cy="add" @click="addLocation('destination')">
                {{ $t('add') }}
            </v-btn>
        </v-col>
        <ModalWrapper v-model="isShowModal">
            <template v-slot:title>
                {{ pointType === 'departure' ? $t('starting_location') : $t('ending_location') }}
            </template>
            <template v-slot:content>
                <InfoPointForm
                    ref="infoPointRef"
                    v-model="editablePoint"
                    :point-type="pointType"
                    :service-type="serviceType"
                />
            </template>
            <template v-slot:btns>
                <v-btn data-cy="add-point" variant="flat" :disabled="!infoPointRef?.valid" @click="savePoint">
                    {{ $t('apply') }}
                </v-btn>
            </template>
        </ModalWrapper>
        <template v-if="serviceType === contractServiceType.ownTransfer">
            <InfoHotelPointModal
                v-model="isShowHotelModal"
                :selected-point="selectedPoint"
                :title="$t('choose_hotels')"
                :subtitle="$t('choose_hotels_note')"
                @save="saveSelectedPoints($event)"
            />
        </template>
    </v-row>
</template>

<script setup lang="ts">
    import {
        initOwnExcursionContractLocation,
        initOwnTransferContractLocation,
    } from '~/utils/types/account/products/helpers'
    import InfoPointForm from '~/components/products/information/InfoPointForm.vue'
    import {nanoid} from 'nanoid'
    import {contractServiceType, StartEndLocation} from '~/utils/types/types'
    import InfoHotelPointModal from '~/components/products/information/InfoHotelPointModal.vue'

    const props = defineProps<{
        serviceType: string
    }>()

    const contract = defineModel<any>()
    const isShowModal = ref<boolean>(false)
    const isShowHotelModal = ref<boolean>(false)
    const pointType = ref<string>('departure')
    const editablePoint = ref<any>(null)
    const selectedPoint = ref(null)
    const isShowValidationError = inject('isShowValidationError', ref(false))
    const infoPointRef = ref(null)

    const {t} = useI18n()

    const savePoint = (): void => {
        const key = pointType.value === 'departure' ? 'departureLocations' : 'destinationLocations'
        if (!editablePoint.value?.timeShift) editablePoint.value.timeShift = '00:00'
        if (editablePoint.value.id) {
            contract.value[key] = contract.value[key].map(item =>
                item.id === editablePoint.value.id ? editablePoint.value : item
            )
        } else contract.value[key].push(Object.assign(editablePoint.value, {id: nanoid()}))

        isShowModal.value = false
    }

    const addLocation = (direction: string, editableLocation = null): void => {
        pointType.value = direction
        if (props.serviceType === contractServiceType.ownExcursion) {
            editablePoint.value = Object.assign(
                initOwnExcursionContractLocation(),
                editableLocation ? editableLocation : {}
            )
        }

        if (props.serviceType === contractServiceType.ownTransfer) {
            editablePoint.value = Object.assign(
                initOwnTransferContractLocation(),
                editableLocation ? editableLocation : {}
            )
        }

        isShowModal.value = true
    }

    const deleteDepartureLocation = (id): void => {
        contract.value.departureLocations = contract.value.departureLocations.filter(location => location.id !== id)
    }

    const deleteDestinationLocation = (id): void => {
        contract.value.destinationLocations = contract.value.destinationLocations.filter(location => location.id !== id)
    }

    const useHeaders = computed(() => {
        return [
            {title: t('name'), value: 'name', minWidth: '120px'},
            {title: t('time_shift'), value: 'timeShift'},
            {title: '', value: 'action', minWidth: '120px', width: '10%'},
        ]
    })

    const getName = (item): string => {
        if (props.serviceType === contractServiceType.ownTransfer) {
            if (item.type === StartEndLocation.Airport) {
                return `${`${t(`transfers.transferPlaceType.${item.type}`)}${item?.locationName ? ` ${item.locationName}` : ''}`}${item?.locationCode ? ` (${item.locationCode})` : ''}, ${item.countryName}, ${item.cityName}`
            }

            return `${
                item.type === StartEndLocation.Manual
                    ? item.name?.en || t(`transfers.transferPlaceType.${item.type}`)
                    : `${t(`transfers.transferPlaceType.${item.type}`)}${item?.locationName ? ` ${item.locationName}` : ''}`
            }, ${item.countryName}, ${item.cityName}`
        }
        if (props.serviceType === contractServiceType.ownExcursion) {
            return `${item.cityName}, ${
                item.type === StartEndLocation.Manual ? item.name?.en : t(`transfers.transferPlaceType.${item.type}`)
            } `
        }
        return ''
    }

    const selectHotel = point => {
        selectedPoint.value = point
        isShowHotelModal.value = true
    }

    const saveSelectedPoints = points => {
        const {selectedPoints} = points
        selectedPoint.value.hotelIds = selectedPoints
        isShowHotelModal.value = false
    }
</script>
